import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Calendar, ShoppingBag, Users, Images, ClipboardCheck, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { sanitizeHtml } from '../lib/sanitizeHtml';

const money = (cents, cur = 'AUD') => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format((cents || 0) / 100);
  } catch (e) { return `$${((cents || 0) / 100).toFixed(2)}`; }
};

function WidgetTitle({ title }) {
  if (!title) return null;
  return (
    <h2 className="font-title text-lg md:text-xl mb-3" style={{ color: 'var(--navy)' }}>{title}</h2>
  );
}

function Loading() {
  return (
    <div className="flex items-center gap-2 text-sm py-4" style={{ color: 'var(--olive)' }}>
      <Loader2 size={15} className="animate-spin" /> Loading…
    </div>
  );
}

function ArticleWidget({ config }) {
  const { getPublicArticle } = useAuth();
  const [html, setHtml] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    setErr(false);
    setHtml(null);
    if (!config.article_id) { setErr(true); return; }
    getPublicArticle(config.article_id).then((a) => { if (live) setHtml(a.body || ''); }).catch(() => live && setErr(true));
    return () => { live = false; };
  }, [config.article_id, getPublicArticle]);
  if (err) return <p className="text-sm" style={{ color: 'var(--olive)' }}>This article is unavailable.</p>;
  if (html === null) return <Loading />;
  return <div className="page-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />;
}

function MembersWidget({ config }) {
  const { getDirectory, mediaUrl } = useAuth();
  const [rows, setRows] = useState(null);
  const [denied, setDenied] = useState(false);
  useEffect(() => {
    let live = true;
    getDirectory().then((d) => { if (live) setRows(Array.isArray(d) ? d : []); })
      .catch((e) => { if (live) { const st = e?.response?.status; if (st === 403 || st === 401) setDenied(true); setRows([]); } });
    return () => { live = false; };
  }, [getDirectory]);
  if (denied) return <p className="text-sm" style={{ color: 'var(--olive)' }} data-testid="members-widget-denied">The members directory is available to financial members.</p>;
  if (rows === null) return <Loading />;
  const limit = Number(config.limit) || 12;
  const shown = rows.slice(0, limit);
  if (!shown.length) return <p className="text-sm" style={{ color: 'var(--olive)' }}>No members to show yet.</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
      {shown.map((m) => (
        <Link key={m.user_id} to={`/members/directory/${m.user_id}`} className="block text-center group">
          <div className="w-full aspect-square rounded-lg overflow-hidden mb-2 bg-white shadow-sm" style={{ border: '1px solid rgba(0,0,0,0.08)' }}>
            {m.photo_url ? (
              <img src={mediaUrl(m.photo_url)} alt={m.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
            ) : (
              <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--sage)' }}>
                <Users size={26} style={{ color: 'var(--olive)' }} />
              </div>
            )}
          </div>
          <div className="text-sm font-title leading-tight" style={{ color: 'var(--maroon)' }}>{m.name}</div>
          {m.rank && <div className="text-xs" style={{ color: 'var(--olive)' }}>{m.rank}</div>}
        </Link>
      ))}
    </div>
  );
}

function ShopWidget({ config }) {
  const { getShopProducts, mediaUrl, org } = useAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let live = true;
    getShopProducts().then((d) => { if (live) setRows(Array.isArray(d) ? d.filter((p) => p.active) : []); }).catch(() => live && setRows([]));
    return () => { live = false; };
  }, [getShopProducts]);
  if (rows === null) return <Loading />;
  const limit = Number(config.limit) || 6;
  const shown = rows.slice(0, limit);
  if (!shown.length) return <p className="text-sm" style={{ color: 'var(--olive)' }}>No products available.</p>;
  const cur = (org && org.currency) || 'AUD';
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
      {shown.map((p) => (
        <div key={p.id} className="bg-white rounded-lg overflow-hidden shadow-sm" style={{ border: '1px solid rgba(0,0,0,0.08)' }}>
          <div className="w-full aspect-[4/3] bg-black/5">
            {p.image_url ? <img src={mediaUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center"><ShoppingBag size={26} style={{ color: 'var(--olive)' }} /></div>}
          </div>
          <div className="p-3">
            <div className="text-sm font-title" style={{ color: 'var(--maroon)' }}>{p.name}</div>
            <div className="text-sm font-semibold mt-1" style={{ color: 'var(--navy)' }}>{money(p.your_price, cur)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EventsWidget({ config }) {
  const { listEvents, org } = useAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let live = true;
    listEvents().then((d) => { if (live) setRows(Array.isArray(d) ? d : []); }).catch(() => live && setRows([]));
    return () => { live = false; };
  }, [listEvents]);
  if (rows === null) return <Loading />;
  const limit = Number(config.limit) || 5;
  const shown = rows.slice(0, limit);
  if (!shown.length) return <p className="text-sm" style={{ color: 'var(--olive)' }}>No upcoming events.</p>;
  const cur = (org && org.currency) || 'AUD';
  return (
    <div className="space-y-3">
      {shown.map((e) => (
        <div key={e.id} className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm" style={{ border: '1px solid rgba(0,0,0,0.08)' }}>
          <Calendar size={18} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--maroon)' }} />
          <div className="min-w-0">
            <div className="text-sm font-title" style={{ color: 'var(--maroon)' }}>{e.title}</div>
            <div className="text-xs" style={{ color: 'var(--olive)' }}>
              {e.starts_at ? new Date(e.starts_at).toLocaleString() : ''}{e.location ? ` · ${e.location}` : ''}
            </div>
            {typeof e.your_price === 'number' && e.your_price > 0 && (
              <div className="text-xs mt-0.5" style={{ color: 'var(--navy)' }}>{money(e.your_price, cur)}</div>
            )}
          </div>
        </div>
      ))}
      <Link to="/events" className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: 'var(--navy)' }}>
        See all events <ExternalLink size={13} />
      </Link>
    </div>
  );
}

function GalleryWidget({ config }) {
  const { getPhotosByTag, getPhotoGallery, mediaUrl } = useAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let live = true;
    const p = config.tag ? getPhotosByTag(config.tag) : getPhotoGallery();
    p.then((d) => { if (live) setRows(Array.isArray(d) ? d : (d && d.photos) || []); }).catch(() => live && setRows([]));
    return () => { live = false; };
  }, [config.tag, getPhotosByTag, getPhotoGallery]);
  if (rows === null) return <Loading />;
  const limit = Number(config.limit) || 8;
  const shown = rows.slice(0, limit);
  if (!shown.length) return <p className="text-sm" style={{ color: 'var(--olive)' }}>No photos to show.</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      {shown.map((ph, i) => {
        const url = ph.url || ph.image_url || (ph.image_id ? `/api/images/${ph.image_id}` : '');
        return (
          <div key={ph.id || i} className="aspect-square rounded-lg overflow-hidden bg-black/5">
            {url && <img src={mediaUrl(url)} alt={ph.title || ph.description || 'Photo'} className="w-full h-full object-cover" />}
          </div>
        );
      })}
    </div>
  );
}

function ApprovalsWidget() {
  const { isStaff, adminListPhotos, adminListSubgroupRequests } = useAuth();
  const [photos, setPhotos] = useState(0);
  const [reqs, setReqs] = useState(0);
  useEffect(() => {
    let live = true;
    if (!isStaff) return;
    adminListPhotos('pending').then((d) => live && setPhotos((d || []).length)).catch(() => {});
    adminListSubgroupRequests().then((d) => live && setReqs((d || []).filter((r) => r.status === 'pending').length)).catch(() => {});
    return () => { live = false; };
  }, [isStaff, adminListPhotos, adminListSubgroupRequests]);
  if (!isStaff) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="approvals-widget">
      <Link to="/admin/photos" className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm hover:shadow transition-shadow" style={{ border: '1px solid rgba(0,0,0,0.08)' }}>
        <ClipboardCheck size={22} style={{ color: 'var(--maroon)' }} />
        <div><div className="text-2xl font-title" style={{ color: 'var(--navy)' }}>{photos}</div><div className="text-xs" style={{ color: 'var(--olive)' }}>Photos awaiting approval</div></div>
      </Link>
      <Link to="/admin/subgroups" className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm hover:shadow transition-shadow" style={{ border: '1px solid rgba(0,0,0,0.08)' }}>
        <ClipboardCheck size={22} style={{ color: 'var(--maroon)' }} />
        <div><div className="text-2xl font-title" style={{ color: 'var(--navy)' }}>{reqs}</div><div className="text-xs" style={{ color: 'var(--olive)' }}>Group-site requests</div></div>
      </Link>
    </div>
  );
}

function Widget({ w }) {
  const { type, title, config = {} } = w;
  const inner = (() => {
    switch (type) {
      case 'heading':
        return config.level === 3
          ? <h3 className="font-title text-lg md:text-xl" style={{ color: 'var(--maroon)' }}>{config.text}</h3>
          : <h2 className="font-title text-2xl md:text-3xl" style={{ color: 'var(--maroon)' }}>{config.text}</h2>;
      case 'divider':
        return <div className="h-0.5 w-full my-2" style={{ background: 'var(--gold)', opacity: 0.6 }} />;
      case 'image':
        return config.url ? (
          <figure>
            <img src={config.url} alt={config.caption || ''} className="w-full rounded-lg shadow-sm" style={config.width ? { maxWidth: config.width, margin: '0 auto' } : undefined} />
            {config.caption && <figcaption className="text-xs text-center mt-2" style={{ color: 'var(--olive)' }}>{config.caption}</figcaption>}
          </figure>
        ) : null;
      case 'richtext':
        return <div className="page-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(config.html || '') }} />;
      case 'article': return <ArticleWidget config={config} />;
      case 'members': return <MembersWidget config={config} />;
      case 'shop': return <ShopWidget config={config} />;
      case 'events': return <EventsWidget config={config} />;
      case 'gallery': return <GalleryWidget config={config} />;
      case 'approvals': return <ApprovalsWidget />;
      default: return null;
    }
  })();
  if (inner === null) return null;
  return (
    <>
      {type !== 'heading' && <WidgetTitle title={title} />}
      {inner}
    </>
  );
}

// A widget's responsive column span (1..12). On mobile everything is full-width;
// the grid reflows automatically as the page/window resizes.
export function widgetSpan(w) {
  const s = Number((w.config || {}).span);
  return s >= 1 && s <= 12 ? s : 12;
}

export function WidgetView({ w }) { return <Widget w={w} />; }

export default function PageWidgets({ widgets }) {
  if (!Array.isArray(widgets) || !widgets.length) return null;
  return (
    <div className="pw-grid mt-2" data-testid="page-widgets">
      {widgets.map((w) => (
        <div key={w.id} className="pw-cell" style={{ '--pw-span': widgetSpan(w) }} data-testid={`widget-${w.type}`}>
          <Widget w={w} />
        </div>
      ))}
    </div>
  );
}

export const WIDGET_ICON = { heading: 'H', richtext: 'T', article: 'A', members: Users, shop: ShoppingBag, events: Calendar, gallery: Images, image: Images, divider: '—', approvals: ClipboardCheck };
